import jsPDF from 'jspdf';
import { SET_CATEGORIES, getClientDescription } from './constants/blockTypes';
import { demoSources } from './utils/demoMedia';
import cipherLogo from './assets/cipher-icon-only-transparent.png';
import { dayTitle } from './utils/dayTitle';

// The picture is drawn about 22 mm wide, so anything larger only makes the file heavier.
const PICTURE_MAX_PX = 320;

// Helper function to turn a demo picture (the still made beside each video, or the first frame
// of a GIF) into a small JPEG for the PDF. White behind it: a GIF's see-through parts would
// otherwise come out black.
const extractFirstFrame = (gifUrl) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const scale = Math.min(1, PICTURE_MAX_PX / Math.max(img.width, img.height, 1));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(img.width * scale));
        canvas.height = Math.max(1, Math.round(img.height * scale));
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
        resolve(dataUrl);
      } catch (e) {
        console.error('Error extracting first frame:', e);
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = gifUrl;
  });
};

// Helper function to fetch image and convert to base64 (unused for GIFs now, kept for other images)
// eslint-disable-next-line no-unused-vars
const fetchImageAsBase64 = async (url) => {
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Error fetching image:', error);
    return null;
  }
};

// The Cipher mark for the header: the part of the 512 px logo file the dumbbell actually fills,
// laid on the header's own navy and stored small as a JPEG (as a see-through PNG it made every
// PDF about 230 kB heavier). Loaded once; if it cannot be loaded the header carries the name alone.
const NAVY = [10, 14, 39];
const LOGO_CROP = { x: 55, y: 195, width: 335, height: 190 };
const LOGO_PX_HIGH = 150;
let logoPromise = null;
const loadLogo = () => {
  if (!logoPromise) {
    logoPromise = new Promise((resolve) => {
      const giveUp = setTimeout(() => resolve(null), 4000);
      const done = (value) => { clearTimeout(giveUp); resolve(value); };
      const img = new Image();
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.height = LOGO_PX_HIGH;
          canvas.width = Math.round(LOGO_PX_HIGH * (LOGO_CROP.width / LOGO_CROP.height));
          const ctx = canvas.getContext('2d');
          ctx.fillStyle = `rgb(${NAVY.join(',')})`;
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, LOGO_CROP.x, LOGO_CROP.y, LOGO_CROP.width, LOGO_CROP.height, 0, 0, canvas.width, canvas.height);
          done(canvas.toDataURL('image/jpeg', 0.92));
        } catch (e) {
          done(null);
        }
      };
      img.onerror = () => done(null);
      img.src = cipherLogo;
    });
  }
  return logoPromise;
};

// Cipher's colours: gold on the app's dark navy.
const colors = {
  gold: '#D4AF37',
  goldRGB: [212, 175, 55],
  darkGray: '#1C1C1E',
  darkGrayRGB: [28, 28, 30],
  navyRGB: NAVY,
  silver: '#8E8E93',
  silverRGB: [142, 146, 147],
  lightGold: '#E5C158',
  lightGoldRGB: [229, 193, 88],
};

// Layout: compact on purpose (the owner's ask of 9 October 2026). A slim header, small day and
// block titles, and each exercise as one row with its picture beside it, so a training day fits
// on a sheet with no large gaps. All sizes are millimetres unless they are font sizes.
const LAYOUT = {
  margin: 14,
  headerHeight: 16,
  headerLogoHeight: 9,
  topAfterHeader: 23, // where content starts after header
  bottom: 16,         // kept clear for the footer

  titleFont: 13,
  dayBarHeight: 6.5,
  dayFont: 10,
  blockFont: 9,
  exerciseFont: 10,
  smallFont: 8,

  pictureSize: 15,
  rowGap: 1.5,
};

// `images: false` leaves the pictures out (and asks for none). `preparedFor` puts a person's
// name and the date at the foot of every page, for a client's own copy.
export const generateProgramPDF = async (program, weeks, { createdAt, images = true, preparedFor } = {}) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = LAYOUT.margin;
  const contentWidth = pageWidth - (margin * 2);
  let currentY = margin;

  // The header on every page: Cipher's mark and name on the app's navy.
  const logo = await loadLogo();
  const addHeader = () => {
    doc.setFillColor(...colors.navyRGB);
    doc.rect(0, 0, pageWidth, LAYOUT.headerHeight, 'F');

    let nameX = margin;
    if (logo) {
      try {
        const logoWidth = LAYOUT.headerLogoHeight * (LOGO_CROP.width / LOGO_CROP.height);
        doc.addImage(logo, 'JPEG', margin, (LAYOUT.headerHeight - LAYOUT.headerLogoHeight) / 2, logoWidth, LAYOUT.headerLogoHeight);
        nameX = margin + logoWidth + 4;
      } catch (error) {
        console.error('Error adding logo:', error);
      }
    }
    doc.setTextColor(...colors.goldRGB);
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text('CIPHER', nameX, (LAYOUT.headerHeight / 2) + 1.7, { charSpace: 1.4 });
  };

  // Add header to first page
  addHeader();
  currentY = LAYOUT.topAfterHeader;

  // Program Title
  doc.setFontSize(LAYOUT.titleFont);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...colors.goldRGB);
  const titleLines = doc.splitTextToSize(program.name || 'Training Program', contentWidth);
  doc.text(titleLines, margin, currentY);
  currentY += (titleLines.length * 5.5) + 0.5;

  // Program Description
  if (program.description) {
    doc.setTextColor(...colors.darkGrayRGB);
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'normal');
    const descLines = doc.splitTextToSize(program.description, contentWidth);
    doc.text(descLines, margin, currentY);
    currentY += (descLines.length * 3.6) + 1.5;
  }


  // Separator line
  doc.setDrawColor(...colors.silverRGB);
  doc.setLineWidth(0.3);
  doc.line(margin, currentY, pageWidth - margin, currentY);
  currentY += 5;

  // Pre-fetch all exercise GIF images (extract first frame as static PNG for PDF)
  const gifCache = new Map();
  const allExercises = weeks.flatMap(w =>
    (w.days || []).flatMap(d => {
      // Handle both block-based structure (new) and exercise-based structure (legacy)
      if (d.blocks && d.blocks.length > 0) {
        return d.blocks.flatMap(block =>
          (block.items || []).map(item => ({
            exercise: item.exercise || { id: item.exercise_id, name: item.exercise_name }
          }))
        );
      } else {
        return d.exercises || [];
      }
    })
  );
  const gifExercises = images ? allExercises.filter(pe => pe.exercise && pe.exercise.gif_url) : [];

  for (const pe of gifExercises) {
    if (gifCache.has(pe.exercise.id)) continue; // the same exercise on another day
    try {
      let gifUrl = pe.exercise.gif_url;
      // Convert relative paths to absolute URLs
      if (gifUrl.startsWith('/')) {
        gifUrl = window.location.origin + gifUrl;
      }
      // The small still made beside each video (about 50 kB) when there is one; otherwise the
      // first frame of the GIF, which means downloading the whole GIF (animated GIFs don't
      // work reliably in PDFs).
      const { poster } = demoSources(gifUrl);
      const staticFrame = (poster && await extractFirstFrame(poster)) || await extractFirstFrame(gifUrl);
      if (staticFrame) {
        gifCache.set(pe.exercise.id, staticFrame);
      }
    } catch (e) {
      console.error('Failed to extract GIF frame:', e);
    }
  }

  // Flatten all days from all weeks (remove week structure)
  const allDays = weeks.flatMap(week => week.days || []);

  // Colours that tell one block from the next (cycling).
  const blockColors = [
    [204, 85, 0],   // orange
    [0, 71, 171],   // blue
    [0, 100, 0],    // green
    [139, 0, 69],   // pink
    [128, 128, 0],  // olive
  ];
  const P = LAYOUT.pictureSize;
  // With pictures on, every row keeps the picture column, so the names line up down the page.
  const textX = images ? margin + P + 4 : margin + 2;
  const setsColumn = 34; // room on the right for "4 sets x 8 reps"

  // The groups of a day: a block each for the block format; one unnamed group for the older format.
  const groupsOf = (day) => {
    if (day.blocks && day.blocks.length > 0) {
      return day.blocks.map((block, blockIndex) => {
        const items = (block.items || []).filter(item => item.exercise && item.exercise.name);
        // A special block (superset, tri-set, ...) with several exercises is done as one set.
        const special = items.length > 1 && block.set_category && block.set_category !== 'normal';
        const rest = block.rest_seconds == null ? null
          : special ? `Rest ${block.rest_seconds}s after all exercises in the set`
            : items.length > 1 ? `Rest ${block.rest_seconds}s between sets of each exercise`
              : `Rest ${block.rest_seconds}s between sets`;
        return {
          title: `Block ${String.fromCharCode(65 + blockIndex)}`,
          category: special ? (SET_CATEGORIES[block.set_category] || SET_CATEGORIES.normal).label : null,
          description: special ? getClientDescription(block.set_category) : null,
          rest,
          color: blockColors[blockIndex % blockColors.length],
          rows: items.map(item => ({ label: item.position_label || '', exercise: item.exercise, sets: item.sets, reps: item.reps, notes: item.notes })),
        };
      }).filter(group => group.rows.length > 0);
    }
    const rows = (day.exercises || [])
      .filter(pe => pe.exercise && pe.exercise.name)
      .map((pe, i) => ({ label: `${i + 1}.`, exercise: pe.exercise, sets: pe.sets, reps: pe.reps, notes: pe.notes }));
    return rows.length > 0 ? [{ title: null, rows }] : [];
  };

  // What one exercise row holds, measured before it is drawn so it is never split across pages.
  const measureRow = (row) => {
    const nameWidth = contentWidth - (textX - margin) - setsColumn;
    doc.setFontSize(LAYOUT.exerciseFont);
    doc.setFont('helvetica', 'bold');
    const nameLines = doc.splitTextToSize(`${row.label} ${row.exercise.name}`.trim(), nameWidth);
    doc.setFontSize(LAYOUT.smallFont);
    doc.setFont('helvetica', 'italic');
    const noteLines = row.notes ? doc.splitTextToSize(`Note: ${row.notes}`, contentWidth - (textX - margin)) : [];
    const hasVideo = !!row.exercise.video_url;
    const textHeight = (nameLines.length * 4.2) + (noteLines.length * 3.4) + (hasVideo ? 3.8 : 0);
    const hasPicture = gifCache.has(row.exercise.id);
    return { nameLines, noteLines, hasVideo, hasPicture, textHeight, height: Math.max(images ? P : 0, textHeight + 1) };
  };

  const drawRow = (row, m) => {
    const top = currentY;
    if (m.hasPicture) {
      try {
        doc.addImage(gifCache.get(row.exercise.id), 'JPEG', margin, top, P, P);
      } catch (e) {
        console.warn('Could not add picture for:', row.exercise.name, e);
      }
    }
    // The text sits in the middle of the row's height, beside the picture.
    let y = top + Math.max(0, (m.height - m.textHeight) / 2) + 3.2;

    doc.setTextColor(...colors.darkGrayRGB);
    doc.setFontSize(LAYOUT.exerciseFont);
    doc.setFont('helvetica', 'bold');
    doc.text(m.nameLines, textX, y);
    if (row.sets != null || row.reps != null) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9.5);
      doc.text(`${row.sets ?? '-'} sets x ${row.reps ?? '-'} reps`, pageWidth - margin, y, { align: 'right' });
    }
    y += m.nameLines.length * 4.2;

    if (m.noteLines.length > 0) {
      doc.setTextColor(100, 100, 100);
      doc.setFontSize(LAYOUT.smallFont);
      doc.setFont('helvetica', 'italic');
      doc.text(m.noteLines, textX, y);
      y += m.noteLines.length * 3.4;
    }
    if (m.hasVideo) {
      doc.setTextColor(0, 0, 255);
      doc.setFontSize(LAYOUT.smallFont);
      doc.setFont('helvetica', 'normal');
      doc.textWithLink('Watch video', textX, y, { url: row.exercise.video_url });
    }
    currentY = top + m.height;
  };

  const startPage = () => {
    doc.addPage();
    addHeader();
    currentY = LAYOUT.topAfterHeader;
  };

  allDays.forEach((day, dayIndex) => {
    // A sheet per training day.
    if (dayIndex > 0) startPage();

    // Day title: one slim bar.
    doc.setFillColor(235, 235, 235);
    doc.rect(margin, currentY, contentWidth, LAYOUT.dayBarHeight, 'F');
    doc.setTextColor(...colors.darkGrayRGB);
    doc.setFontSize(LAYOUT.dayFont);
    doc.setFont('helvetica', 'bold');
    doc.text(`Day ${day.day_number}: ${dayTitle(day) || 'Unnamed'}`, margin + 2.5, currentY + 4.6);
    currentY += LAYOUT.dayBarHeight + 2.5;

    if (day.notes) {
      doc.setTextColor(100, 100, 100);
      doc.setFontSize(LAYOUT.smallFont);
      doc.setFont('helvetica', 'italic');
      const dayNotes = doc.splitTextToSize(day.notes, contentWidth - 4);
      doc.text(dayNotes, margin + 2.5, currentY + 2);
      currentY += (dayNotes.length * 3.4) + 2.5;
    }

    groupsOf(day).forEach((group) => {
      const measured = group.rows.map(measureRow);
      const titleHeight = group.title ? (group.description ? 8.4 : 5.2) : 0;

      // A block's title is never left alone at the foot of a page.
      if (currentY + titleHeight + measured[0].height > pageHeight - LAYOUT.bottom) startPage();

      if (group.title) {
        doc.setFillColor(...group.color);
        doc.rect(margin, currentY, 1.2, group.description ? 7.4 : 4.2, 'F');
        doc.setTextColor(...group.color);
        doc.setFontSize(LAYOUT.blockFont);
        doc.setFont('helvetica', 'bold');
        doc.text(group.title, margin + 3, currentY + 3.3);
        let x = margin + 3 + doc.getTextWidth(group.title);
        if (group.category) {
          doc.setFont('helvetica', 'normal');
          doc.text(` - ${group.category}`, x, currentY + 3.3);
          x += doc.getTextWidth(` - ${group.category}`);
        }
        if (group.rest) {
          doc.setTextColor(90, 90, 90);
          doc.setFontSize(LAYOUT.smallFont);
          doc.setFont('helvetica', 'italic');
          doc.text(group.rest, pageWidth - margin, currentY + 3.3, { align: 'right' });
        }
        if (group.description) {
          doc.setTextColor(90, 90, 90);
          doc.setFontSize(7.5);
          doc.setFont('helvetica', 'italic');
          doc.text(doc.splitTextToSize(group.description, contentWidth - 4)[0], margin + 3, currentY + 6.9);
        }
        currentY += titleHeight;
      }

      group.rows.forEach((row, i) => {
        const m = measured[i];
        if (currentY + m.height > pageHeight - LAYOUT.bottom) startPage();
        drawRow(row, m);
        if (i < group.rows.length - 1) {
          doc.setDrawColor(225, 225, 225);
          doc.setLineWidth(0.2);
          doc.line(textX, currentY + (LAYOUT.rowGap / 2), pageWidth - margin, currentY + (LAYOUT.rowGap / 2));
        }
        currentY += LAYOUT.rowGap;
      });
      currentY += 2.5; // between blocks
    });
  });

  // Footer on all pages
  const totalPages = doc.internal.pages.length - 1;
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setTextColor(...colors.silverRGB);
    doc.setFontSize(8);
    doc.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 10, {
      align: 'center',
    });
    doc.text('Cipher', pageWidth - margin, pageHeight - 10, {
      align: 'right',
    });
    if (preparedFor) {
      const on = (createdAt || new Date()).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
      doc.text(`Prepared for ${preparedFor}, ${on}`, margin, pageHeight - 10);
    }
  }

  // Save the PDF
  const fileName = `${program.name?.replace(/[^a-z0-9]/gi, '_') || 'program'}_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(fileName);

  return fileName;
};
